/* -------------------------------------------------------------------------- */
/*             Componente SlidingPaneSelectBranchesToLink                     */
/* -------------------------------------------------------------------------- */
// Este componente permite seleccionar sucursales existentes para vincular a un cliente

import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Row } from 'reactstrap';
import { TableColumn } from 'utility/types';
import CheckSquareButton from 'views/components/CheckSquareButton';
import CollapseFilters from 'views/components/CollapseFilters';
import Toolbar from 'views/components/Toolbar';
import Table from 'views/components/Table';
import DropdownInput from 'views/formComponents/DropdownInput';
import Membership from 'views/formComponents/Membership';
import * as groupSelectors from 'views/screens/Groups/reducers';
import * as locationSelectors from 'views/screens/Locations/reducers';
import FetchEntities from 'views/screens/Entities/views/FetchEntities';
import * as authSelectors from '@redux/reducers/auth';

const columns: TableColumn[] = [
  {
    fieldName: 'locationId',
    headerText: 'common.id',
    isPrimaryKey: true
  },
  {
    fieldName: 'locationName',
    headerText: 'locations.locationName'
  },
  {
    fieldName: 'privateCode',
    headerText: 'locations.privateCode'
  },
  {
    fieldName: 'locationAddress',
    headerText: 'locations.locationAddress'
  }
];

interface Props {
  isOpenSlidingPane: boolean;
  closeSlidingPane: (selectedLocations: any[]) => void;
  excludeCustomerId: number;
  membership: {
    operatorId?: number;
    organizationId?: number;
    divisionId?: number;
    subdivisionId?: number;
  };
}

interface DropdownGroup {
  id: number;
  value: number;
  label: string;
  label_intl: string;
}

const SlidingPaneSelectBranchesToLink = ({
  isOpenSlidingPane,
  closeSlidingPane,
  excludeCustomerId,
  membership: propMembership
}: Props) => {
  const intl = useIntl();
  
  const [selectAll, setSelectAll] = useState<Date>(new Date());
  const [deselectAll, setDeselectAll] = useState<Date>(new Date());
  const [tempSelected, setTempSelected] = useState<any[]>([]);

  const {
    control,
    watch,
    reset
  } = useForm({
    defaultValues: {
      membership: undefined,
      groupId: undefined
    }
  });

  const formValues = watch();
  const groupId = formValues?.groupId;
  const membership = formValues?.membership;

  const { user, locations, groups } = useSelector((state) => ({
    user: authSelectors.getUser(state),
    locations: locationSelectors.getLocationsList(state) || [],
    groups: groupSelectors.getGroupsList(state) || []
  }));

  // Filtrar locations: misma organización, no pertenecen al cliente actual
  const baseAvailableLocations = membership
    ? locations.filter(
        loc =>
          loc.organizationId === membership.organizationId &&
          loc.customerId !== excludeCustomerId
      )
    : [];

  // Filtrar por grupo si está seleccionado
  const availableLocations = groupId
    ? baseAvailableLocations.filter(location =>
        Object.values(groups.find(group => group.groupId === groupId)?.locations ?? {}).some(
          (groupLocation: any) => groupLocation.locationId === location.locationId
        )
      )
    : baseAvailableLocations;

  // Limpiar selección al abrir
  useEffect(() => {
    if (isOpenSlidingPane) {
      setTempSelected([]);
    }
  }, [isOpenSlidingPane]);

  const handleAccept = () => {
    closeSlidingPane(tempSelected);
  };

  const handleCancel = () => {
    reset();
    setTempSelected([]);
    closeSlidingPane([]);
  };

  return (
    <>
      <FetchEntities 
        useLocations 
        locationsFilters={membership}
        useGroups 
        groupsFilters={membership}
      />
      <SlidingPane
        className="custom-sliding-pane custom-sliding-pane--wide"
        overlayClassName="custom-overlay-sliding-pane"
        isOpen={isOpenSlidingPane}
        title={intl.formatMessage({ id: 'customers.selectExistingBranch' })}
        width={'72rem'}
        onRequestClose={handleCancel}
      >
        <>
          {/* Filtros */}
          <CollapseFilters className="mb-1" active>
            <Row>
              <Membership
                control={control}
                name={'membership'}
                shouldUnregister
                options={{
                  colSize: '3',
                  useUserOperator: true,
                  useUserOrganization: true,
                  showAllDivision: true,
                  showAllSubdivision: true
                }}
              />
              <Col sm="3">
                <Controller
                  control={control}
                  name="groupId"
                  render={({ field, fieldState }) => (
                    <DropdownInput
                      field={field}
                      fieldstate={fieldState}
                      id={'groupDropdown'}
                      label={intl.formatMessage({ id: 'groups.group' })}
                      isClearable
                      options={groups
                        .map(
                          (group): DropdownGroup => ({
                            id: group?.groupId ?? -1,
                            value: group?.groupId ?? -1,
                            label: group?.groupName ?? '',
                            label_intl: group?.groupName ?? ''
                          })
                        )}
                    />
                  )}
                />
              </Col>
            </Row>
          </CollapseFilters>

          <Toolbar
            buttons={[
              <CheckSquareButton
                key="select-all"
                tooltipMessage={'common.selectAll'}
                handleClick={() => setSelectAll(new Date())}
              />,
              <CheckSquareButton
                key="deselect-all"
                tooltipMessage={'common.deselectAll'}
                handleClick={() => setDeselectAll(new Date())}
                checked={false}
              />
            ]}
          />
          <Table
            id={'selectBranchesToLinkTable'}
            data={availableLocations}
            columns={columns}
            selected={tempSelected}
            changeSelected={setTempSelected}
            allowPaging={true}
            pageSize={15}
            allowFiltering={true}
            allowMultipleSelection
            selectAll={selectAll}
            deselectAll={deselectAll}
            height={500}
            enablePersistence={false}
            primaryKey={['locationId']}
            persistSelection={true}
          />
          {availableLocations.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
              <div>
                {intl.formatMessage({ id: 'locations.noLocationsAvailable' })}
              </div>
            </div>
          )}
        </>
        <Row className={'pt-1'}>
          <Col>
            <Button.Ripple
              className="me-2 mt-50 mb-50"
              color="primary"
              onClick={handleAccept}
              disabled={tempSelected.length === 0}
            >
              {intl.formatMessage({ id: 'common.accept' })} ({tempSelected.length})
            </Button.Ripple>
            <Button.Ripple
              className="me-2 mt-50 mb-50"
              color="light"
              onClick={handleCancel}
            >
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button.Ripple>
          </Col>
        </Row>
      </SlidingPane>
    </>
  );
};

export default SlidingPaneSelectBranchesToLink;
